import {
  AiPermissionSet,
  AuditLogPermissionSet,
  DutyDelayPermissionSet,
  DutyPermissionSet,
  EdiBrokerConfigurationPermissionSet,
  EdiEligibilityCheckPermissionSet,
  FleetPermissionSet,
  GtfsRealtimePermissionSet,
  NylasAdminGrantPermissionSet,
  PermissionSetType,
  QuickReplyPermissionSet,
  RequestPermissionSet,
  RiderOnboardingFlowPermissionSet,
  SidebarProductVisibilityPermissionSet,
  VehiclePermissionSet,
} from '../sets'
import { AccessGrantPermissionSet } from '../sets/AccessGrantPermissions'
import { AdminUserPermissionSet } from '../sets/AdminUserPermissions'
import { AiAgentPermissionSet } from '../sets/AiAgentPermissions'
import { AnalyticsPermissionSet } from '../sets/AnalyticsPermissions'
import { AnnouncementPermissionSet } from '../sets/AnnouncementPermissions'
import { ApiKeyPermissionSet } from '../sets/ApiKeyPermissions'
import { AppPermissionSet } from '../sets/AppPermissions'
import { AuthorizationGroupMembershipPermissionSet } from '../sets/AuthorizationGroupMembershipPermissions'
import { AuthorizationGroupPermissionSet } from '../sets/AuthorizationGroupPermissions'
import { AuthorizationRolePermissionSet } from '../sets/AuthorizationRolePermissions'
import { CancellationReasonPermissionSet } from '../sets/CancellationReasonPermissions'
import { CustomLinkPermissionSet } from '../sets/CustomLinkPermissions'
import { DataRetentionSchedulePermissionSet } from '../sets/DataRetentionSchedulePermissions'
import { DirectorySyncPermissionSet } from '../sets/DirectorySyncPermissions'
import { FixedRouteForDisplayPermissionSet } from '../sets/FixedRouteForDisplayPermissions'
import { GtfsPermissionsSet } from '../sets/GtfsPermissions'
import { IvrPermissionSet } from '../sets/IvrPermissions'
import { KnowledgeEntryPermissionSet } from '../sets/KnowledgeEntryPermissions'
import { LaunchReportingPermissionSet } from '../sets/LaunchReportingPermissions'
import { MatchingOutcomePermissionSet } from '../sets/MatchingOutcomePermissions'
import { OrganizationPermissionSet } from '../sets/OrganizationPermissions'
import { PaymentMethodTypePermissionSet } from '../sets/PaymentMethodTypePermissions'
import { PhoneNumberPermissionSet } from '../sets/PhoneNumberPermissions'
import { ProviderPermissionSet } from '../sets/ProviderPermissions'
import { RealizePermissionSet } from '../sets/RealizePermissions'
import { TripGroupPermissionSet } from '../sets/TripGroupPermissions'
import { UserFleetAgreementPermissionSet } from '../sets/UserFleetAgreementPermissions'
import { WebhookPermissionSet } from '../sets/WebhookPermissions'
import { WorkflowPermissionSet } from '../sets/WorkflowPermissions'
import { AccessToEngageRole } from './AccessToEngageRole'
import { AccessToResolveRole } from './AccessToResolveRole'
import { EamAdminRole } from './EamAdminRole'
import { FinanceManagerRole } from './FinanceManagerRole'
import { OperationsManagerRole } from './OperationsManagerRole'
import { ServicePlannerRole } from './ServicePlannerRole'

/**
 * Ensure that at the root level of the role, we only add permission sets to it and not
 * individual permissions. Individual permissions at this level will not get assigned to the role.
 * If individual permissions are required, create a permission set of one permission and add to the role.
 */
export const AdministratorRole: PermissionSetType[] = [
  // Permissions from sub roles
  ...OperationsManagerRole,
  ...ServicePlannerRole,
  ...FinanceManagerRole,
  ...AccessToEngageRole,
  ...AccessToResolveRole,
  ...EamAdminRole,

  SidebarProductVisibilityPermissionSet.ShowOperationsProduct,
  SidebarProductVisibilityPermissionSet.ShowAnalyticsProduct,
  SidebarProductVisibilityPermissionSet.ShowEligibilityProduct,
  SidebarProductVisibilityPermissionSet.ShowResolveProduct,
  SidebarProductVisibilityPermissionSet.ShowAnalyzeProduct,
  SidebarProductVisibilityPermissionSet.ShowAnalyticsProduct,
  SidebarProductVisibilityPermissionSet.ShowEamProduct,
  SidebarProductVisibilityPermissionSet.ShowAiProduct,
  SidebarProductVisibilityPermissionSet.ShowAdminProduct,

  AnalyticsPermissionSet.Access,

  LaunchReportingPermissionSet.View,
  LaunchReportingPermissionSet.Write,
  LaunchReportingPermissionSet.Delete,
  LaunchReportingPermissionSet.Export,
  LaunchReportingPermissionSet.CustomExport,

  RealizePermissionSet.Access,

  DirectorySyncPermissionSet.View,
  DirectorySyncPermissionSet.Sync,
  DirectorySyncPermissionSet.Update,

  DataRetentionSchedulePermissionSet.View,
  DataRetentionSchedulePermissionSet.Write,

  OrganizationPermissionSet.WriteCustomSchemas,
  OrganizationPermissionSet.EditOwn,

  AdminUserPermissionSet.View,
  AdminUserPermissionSet.Edit,
  AdminUserPermissionSet.New,
  AdminUserPermissionSet.Delete,
  AdminUserPermissionSet.Import,
  NylasAdminGrantPermissionSet.Write,

  DutyDelayPermissionSet.View,

  ApiKeyPermissionSet.View,
  ApiKeyPermissionSet.Write,
  ApiKeyPermissionSet.Delete,

  WebhookPermissionSet.View,
  WebhookPermissionSet.Write,
  AnnouncementPermissionSet.Write,

  PaymentMethodTypePermissionSet.View,
  PaymentMethodTypePermissionSet.Write,

  RiderOnboardingFlowPermissionSet.Write,

  // Permissions for API Keys.
  // TODO currently api keys and administrators have the same permissions.
  // When we work on scoping down what API Keys can do it would be good to remove this coupling
  // ----------------------------------
  ApiKeyPermissionSet.KeyUser,
  // ----------------------------------

  AppPermissionSet.View,
  AppPermissionSet.AppInstallationView,
  AppPermissionSet.AppInstallationManage,

  UserFleetAgreementPermissionSet.DeleteAll,

  FleetPermissionSet.ForceEdit,

  RequestPermissionSet.ViewDailyCounts,

  MatchingOutcomePermissionSet.View,

  // Slot completion from admin panel
  DutyPermissionSet.CompleteSlot,

  IvrPermissionSet.ViewMetrics,

  RequestPermissionSet.CreateUnmatchedRequest,
  RequestPermissionSet.WriteRestricted,

  VehiclePermissionSet.ManageInspection,
  VehiclePermissionSet.ViewInspection,

  WorkflowPermissionSet.View,
  WorkflowPermissionSet.Write,
  WorkflowPermissionSet.Delete,

  AiPermissionSet.Chat,
  AiAgentPermissionSet.View,
  AiAgentPermissionSet.Write,
  KnowledgeEntryPermissionSet.View,
  KnowledgeEntryPermissionSet.Write,
  PhoneNumberPermissionSet.View,
  PhoneNumberPermissionSet.Configure,
  PhoneNumberPermissionSet.Purchase,
  PhoneNumberPermissionSet.Delete,

  AuditLogPermissionSet.View,
  AuditLogPermissionSet.ViewAlertRules,
  AuditLogPermissionSet.WriteAlertRules,
  AuditLogPermissionSet.ViewAlerts,

  AuthorizationGroupPermissionSet.View,
  AuthorizationGroupPermissionSet.Write,
  AuthorizationGroupPermissionSet.Delete,

  AuthorizationRolePermissionSet.View,
  AuthorizationRolePermissionSet.Write,
  AuthorizationRolePermissionSet.Delete,

  AuthorizationGroupMembershipPermissionSet.View,
  AuthorizationGroupMembershipPermissionSet.Write,

  AccessGrantPermissionSet.View,
  AccessGrantPermissionSet.Write,

  ProviderPermissionSet.View,
  ProviderPermissionSet.Write,

  CancellationReasonPermissionSet.View,
  CancellationReasonPermissionSet.Write,
  CancellationReasonPermissionSet.Delete,

  GtfsRealtimePermissionSet.ViewVehiclePositions,

  TripGroupPermissionSet.View,
  TripGroupPermissionSet.Write,
  TripGroupPermissionSet.Delete,

  EdiBrokerConfigurationPermissionSet.View,
  EdiBrokerConfigurationPermissionSet.Write,

  EdiEligibilityCheckPermissionSet.View,
  EdiEligibilityCheckPermissionSet.Write,

  CustomLinkPermissionSet.View,
  CustomLinkPermissionSet.Write,

  FixedRouteForDisplayPermissionSet.View,
  FixedRouteForDisplayPermissionSet.Write,

  QuickReplyPermissionSet.View,
  QuickReplyPermissionSet.Write,

  GtfsPermissionsSet.View,
]
